import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
  Chip, Badge, FormControl, InputLabel, Select, MenuItem, Collapse, Menu,
  IconButton, Autocomplete,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import {
  crushingWasteLotService, crushingOperationService, stockArrivalService, warehouseService,
} from '../services/api';
import api from '../services/api';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';
import { formatProductionOrderRef, orderRefFromRow } from '../utils/orderLabel';

const SOURCE_WAREHOUSE_ID = 'crushing_waste';

const SELECT_MENU_PROPS = {
  PaperProps: { sx: { maxHeight: 'min(40vh, 320px)' } },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

/** Той самий реєстр, що /warehouses/ і інші склади (без поточного). */
const FALLBACK_TRANSFER_WAREHOUSES = [
  { id: 'production', name: 'Склад виробництва' },
  { id: 'raw', name: 'Склад сировини' },
  { id: 'dyes', name: 'Склад барвників' },
  { id: 'packaging', name: 'Склад упаковки' },
  { id: 'technical', name: 'Склад техматеріалів' },
  { id: 'semi', name: 'Склад напівфабрикатів' },
  { id: 'packaging_work', name: 'Склад для пакування' },
  { id: 'finished', name: 'Склад готової продукції' },
  { id: 'crushing_waste', name: 'Склад перемолів' },
  { id: 'blocking', name: 'Склад блокування' },
  { id: 'consumable', name: 'Склад розхідних матеріалів' },
  { id: 'defect', name: 'Склад браку' },
  { id: 'samples', name: 'Склад взірців' },
].filter((w) => w.id !== SOURCE_WAREHOUSE_ID);

function productLabel(lot) {
  const art = (lot.product_article || '').trim();
  const name = (lot.product_name || '').trim();
  if (art && name) return `${art} — ${name}`;
  return art || name || '—';
}

function fmt(dt) {
  return dt ? new Date(dt).toLocaleString('uk-UA') : '—';
}

function formatComposition(comp) {
  if (!comp || !comp.length) return '—';
  return comp.map((c) => {
    const kind = c.kind === 'dye' ? 'барвник' : 'сировина';
    return `${c.name}: ${c.percentage}% (${kind})`;
  }).join('; ');
}

/** Сировина + барвник (без префікса «Перемол») */
function peremolDisplayName(comp, fallback) {
  if (fallback) return fallback;
  const raws = (comp || []).filter((c) => c.kind !== 'dye').map((c) => c.name).filter(Boolean);
  const dyes = (comp || []).filter((c) => c.kind === 'dye').map((c) => c.name).filter(Boolean);
  const parts = [...raws, ...dyes];
  return parts.length ? parts.join(' + ') : 'без складу';
}

function compositionGroupKey(lot) {
  if (lot.composition_key) return lot.composition_key;
  const raws = (lot.composition || [])
    .filter((c) => c.kind !== 'dye')
    .map((c) => c.raw_material_id || c.name || '')
    .filter(Boolean)
    .sort()
    .join(',');
  const dyes = (lot.composition || [])
    .filter((c) => c.kind === 'dye')
    .map((c) => c.dye_id || c.name || '')
    .filter(Boolean)
    .sort()
    .join(',');
  return `raw|${raws}|dye|${dyes}`;
}

function StockCrushingWastePage() {
  const navigate = useNavigate();
  const [lots, setLots] = useState([]);
  const [operations, setOperations] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [loading, setLoading] = useState(true);

  // tabs
  const [tab, setTab] = useState(0);

  // залишки — фільтри / розгортання груп
  const [search, setSearch] = useState('');
  const [qtyFilter, setQtyFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // операції — фільтри
  const [opDateFrom, setOpDateFrom] = useState('');
  const [opDateTo, setOpDateTo] = useState('');
  const [opConfirmed, setOpConfirmed] = useState('all'); // all | yes | no

  // detail dialog (склад партії)
  const [detail, setDetail] = useState(null);

  // arrival confirm dialog
  const [arrivalDlg, setArrivalDlg] = useState(null);
  const [arrivalQty, setArrivalQty] = useState('');

  // Header buttons state
  const [transferHubOpen, setTransferHubOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferGroupKey, setTransferGroupKey] = useState('');
  const [transferQty, setTransferQty] = useState('');
  const [transferDest, setTransferDest] = useState('production');
  const [transferNote, setTransferNote] = useState('');
  const [transferError, setTransferError] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferDestOptions, setTransferDestOptions] = useState(FALLBACK_TRANSFER_WAREHOUSES);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [infoDialog, setInfoDialog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lotsRes, opsRes, arrRes] = await Promise.all([
        crushingWasteLotService.getAll({ show_all: true, transferred_to_crushing: true }),
        crushingOperationService.getAll(),
        stockArrivalService.getAll({ crushing_only: true }),
      ]);
      setLots(lotsRes.data.results ?? lotsRes.data);
      setOperations(opsRes.data.results ?? opsRes.data);
      const all = arrRes.data.results ?? arrRes.data;
      setArrivals(Array.isArray(all) ? all.filter((a) => a.crushing_operation != null) : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    warehouseService.getAll({ exclude: SOURCE_WAREHOUSE_ID })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        if (list.length) setTransferDestOptions(list.filter((w) => w.id !== SOURCE_WAREHOUSE_ID));
      })
      .catch(() => setTransferDestOptions(FALLBACK_TRANSFER_WAREHOUSES));
  }, []);

  // ── Залишки: групування за складом (Перемол + сировина + барвник) ─────────
  const groupedLots = useMemo(() => {
    const map = new Map();
    for (const lot of lots) {
      const key = compositionGroupKey(lot);
      if (!map.has(key)) {
        map.set(key, {
          key,
          peremol_name: peremolDisplayName(lot.composition, lot.peremol_name),
          composition: lot.composition || [],
          quantity_kg: 0,
          quantity_initial: 0,
          lot_ids: [],
          order_ids: [],
          items: [],
          created_at: lot.created_at,
          // для діалогу деталей %
          id: lot.id,
          product_name: lot.product_name,
        });
      }
      const g = map.get(key);
      g.quantity_kg += Number(lot.quantity_kg) || 0;
      g.quantity_initial += Number(lot.quantity_initial) || 0;
      g.lot_ids.push(lot.id);
      g.items.push(lot);
      if (lot.production_order_id != null) g.order_ids.push(lot.production_order_id);
      if (lot.created_at && (!g.created_at || lot.created_at < g.created_at)) {
        g.created_at = lot.created_at;
      }
    }
    return Array.from(map.values()).map((g) => ({
      ...g,
      items: [...g.items].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))),
      quantity_kg: Math.round(g.quantity_kg * 1000) / 1000,
      quantity_initial: Math.round(g.quantity_initial * 1000) / 1000,
    }));
  }, [lots]);

  const filteredLots = useMemo(() => {
    let data = [...groupedLots];

    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter((l) =>
        (l.peremol_name || '').toLowerCase().includes(q) ||
        (l.product_name || '').toLowerCase().includes(q) ||
        l.lot_ids.some((id) => String(id).includes(q)) ||
        l.order_ids.some((id) => String(id).includes(q)) ||
        l.items.some((it) => productLabel(it).toLowerCase().includes(q))
      );
    }

    if (qtyFilter === 'gt0') data = data.filter((l) => l.quantity_kg > 0);
    else if (qtyFilter === 'eq0') data = data.filter((l) => l.quantity_kg === 0);
    else if (qtyFilter === 'lt0') data = data.filter((l) => l.quantity_kg < 0);

    data.sort((a, b) => {
      let av, bv;
      if (sortKey === 'name') { av = a.peremol_name || ''; bv = b.peremol_name || ''; }
      else if (sortKey === 'qty') { av = a.quantity_kg; bv = b.quantity_kg; }
      else { av = a.created_at || ''; bv = b.created_at || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [groupedLots, search, qtyFilter, sortKey, sortDir]);

  const transferMaterialOptions = useMemo(
    () => groupedLots.filter((g) => Number(g.quantity_kg) > 0),
    [groupedLots],
  );

  const openManualTransfer = () => {
    setTransferError('');
    setTransferQty('');
    setTransferNote('');
    setTransferDest(transferDestOptions[0]?.id || 'production');
    const first = transferMaterialOptions[0];
    setTransferGroupKey(first?.key || '');
    setTransferOpen(true);
  };

  const handleManualTransfer = async () => {
    const group = groupedLots.find((g) => g.key === transferGroupKey);
    if (!group) {
      setTransferError('Оберіть перемол');
      return;
    }
    const qty = parseFloat(transferQty);
    if (!Number.isFinite(qty) || qty <= 0) {
      setTransferError('Вкажіть коректну кількість');
      return;
    }
    if (qty > Number(group.quantity_kg) + 1e-9) {
      setTransferError(`Доступно лише ${group.quantity_kg} кг`);
      return;
    }
    if (!transferDest) {
      setTransferError('Оберіть склад призначення');
      return;
    }
    setTransferLoading(true);
    setTransferError('');
    try {
      await crushingWasteLotService.transferOut({
        lot_ids: group.lot_ids,
        quantity: qty,
        destination: transferDest,
        note: transferNote,
      });
      setTransferOpen(false);
      setTransferHubOpen(false);
      await load();
    } catch (e) {
      setTransferError(e.response?.data?.error || e.message || 'Помилка переміщення');
    } finally {
      setTransferLoading(false);
    }
  };

  // ── Операції ─────────────────────────────────────────────────────────────
  const filteredOps = useMemo(() => {
    let data = [...operations];
    if (opConfirmed === 'yes') data = data.filter((o) => o.confirmed_at);
    else if (opConfirmed === 'no') data = data.filter((o) => !o.confirmed_at);
    if (opDateFrom) data = data.filter((o) => (o.performed_at || o.created_at) >= opDateFrom);
    if (opDateTo)   data = data.filter((o) => (o.performed_at || o.created_at) <= opDateTo + 'T23:59:59');
    return data.sort((a, b) => (b.performed_at || b.created_at).localeCompare(a.performed_at || a.created_at));
  }, [operations, opConfirmed, opDateFrom, opDateTo]);

  // ── Надходження ───────────────────────────────────────────────────────────
  const pendingArrivals = useMemo(() => arrivals.filter((a) => a.status === 'pending' || a._isWasteLot), [arrivals]);

  const handleConfirmArrival = async () => {
    if (!arrivalDlg) return;
    try {
      if (arrivalDlg._isWasteLot) {
        await api.post(`/crushing-waste-lots/${arrivalDlg.id}/confirm-arrival/`);
      } else {
        await stockArrivalService.confirm(arrivalDlg.id, { quantity_actual: parseFloat(arrivalQty) });
      }
      setArrivalDlg(null);
      setArrivalQty('');
      load();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="280px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Склад перемолів</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={shortageOpen ? 'contained' : 'outlined'} color="warning" startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (!shortageOpen) {
                setShortageLoading(true);
                api.get('/material-shortages/').then(r => setShortages(r.data || [])).catch(() => setShortages([])).finally(() => setShortageLoading(false));
              }
              setShortageOpen(o => !o);
            }}>
            Нестача {shortages.length > 0 && !shortageOpen ? `(${shortages.length})` : ''}
          </Button>
          <Button variant="contained" color="primary" startIcon={<MenuBookIcon />} onClick={(e) => setRefsAnchorEl(e.currentTarget)}>
            Довідники
          </Button>
          <Menu anchorEl={refsAnchorEl} open={refsMenuOpen} onClose={() => setRefsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {[
              ['/products', 'Вироби'],
              ['/raw-materials', 'Сировина'],
              ['/dyes', 'Фарби'],
              ['/packaging-materials', 'Упаковка'],
              ['/filament', 'Філамент'],
              ['/consumable-materials', 'Розхідні матеріали'],
            ].map(([path, label]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  setRefsAnchorEl(null);
                  navigateWithReturn(navigate, path, stockReturnTo('crushing-waste'), stockReturnLabel('crushing-waste'));
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="contained" color="success" startIcon={<InputIcon />}
            onClick={() => setInfoDialog({ title: 'Прихід — Склад перемолів', text: 'Після завершення дроблення зʼявляється рядок у вкладці «Надходження». Натисніть «Прийняти» — тоді партія потрапить у «Залишки». До прийняття залишок не змінюється.' })}>
            Прихід
          </Button>
          <Button variant="contained" color="error" startIcon={<OutputIcon />}
            onClick={() => setInfoDialog({ title: 'Вибуття — Склад перемолів', text: 'Вибуття відбувається через операцію дроблення. Перегляньте вкладку «Операції».' })}>
            Вибуття
          </Button>
          <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />} onClick={() => setTransferHubOpen(true)}>
            Переміщення
          </Button>
        </Box>
      </Box>
      {shortageOpen && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label="Активний фільтр: Нестача"
            color="warning"
            onDelete={() => setShortageOpen(false)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      <Collapse in={shortageOpen}>
        <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
              ⚠️ Нестача матеріалів для запланованих виробничих замовлень
            </Typography>
          </Box>
          {shortageLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : shortages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="success.main" fontWeight={600}>✅ Нестач немає</Typography>
            </Box>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell>Матеріал</TableCell><TableCell align="right">Потрібно</TableCell>
                <TableCell align="right">Є на складі</TableCell><TableCell align="right">Нестача</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shortages.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.material_name || s.name}</TableCell>
                    <TableCell align="right">{s.required_quantity ?? '—'}</TableCell>
                    <TableCell align="right">{s.available_quantity ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{s.shortage ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )}
        </Box>
      </Collapse>

      {/* Filters */}
      {tab === 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Пошук по складу / назві / ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: '1 1 280px' }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Залишок</InputLabel>
            <Select label="Залишок" value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
              <MenuItem value="all">Усі</MenuItem>
              <MenuItem value="gt0">Тільки &gt; 0</MenuItem>
              <MenuItem value="eq0">Тільки = 0</MenuItem>
              <MenuItem value="lt0">Тільки &lt; 0</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Сортування</InputLabel>
            <Select label="Сортування" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              <MenuItem value="created">За датою</MenuItem>
              <MenuItem value="name">За назвою</MenuItem>
              <MenuItem value="qty">За залишком</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Напрям</InputLabel>
            <Select label="Напрям" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
              <MenuItem value="desc">↓ Спадання</MenuItem>
              <MenuItem value="asc">↑ Зростання</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => {
            setSearch(''); setQtyFilter('all'); setSortKey('created'); setSortDir('desc');
          }}>Скинути</Button>
        </Box>
      )}

      {tab === 1 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Статус</InputLabel>
            <Select label="Статус" value={opConfirmed} onChange={(e) => setOpConfirmed(e.target.value)}>
              <MenuItem value="all">Усі</MenuItem>
              <MenuItem value="no">Не підтверджені</MenuItem>
              <MenuItem value="yes">Підтверджені</MenuItem>
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="З" value={opDateFrom}
            onChange={(e) => setOpDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="По" value={opDateTo}
            onChange={(e) => setOpDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          {(opDateFrom || opDateTo) && (
            <Button size="small" onClick={() => { setOpDateFrom(''); setOpDateTo(''); }}
              sx={{ color: '#999', textTransform: 'none' }}>Скинути дати</Button>
          )}
        </Box>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label={`Залишки (${filteredLots.length})`} />
          <Tab label={`Операції (${filteredOps.length})`} />
          <Tab label={
            <Badge color="error" badgeContent={pendingArrivals.length || 0}>
              <Box sx={{ pr: pendingArrivals.length ? 1 : 0 }}>Надходження</Box>
            </Badge>
          } />
        </Tabs>
      </Box>

      {/* ── Tab 0: Залишки ── */}
      {tab === 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48 }} />
                <TableCell>Партії</TableCell>
                <TableCell>Замовлення</TableCell>
                <TableCell>Виріб (склад перемолу)</TableCell>
                <TableCell>Залишок (кг)</TableCell>
                <TableCell>Початково (кг)</TableCell>
                <TableCell>Створено</TableCell>
                <TableCell align="right">Склад</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">Нічого не знайдено</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredLots.map((row) => {
                const open = expandedGroups.has(row.key);
                const canExpand = (row.items?.length || 0) > 0;
                return (
                  <React.Fragment key={row.key}>
                    <TableRow
                      hover
                      onClick={() => canExpand && toggleGroup(row.key)}
                      sx={{ cursor: canExpand ? 'pointer' : 'default', '& > *': { borderBottom: open ? 'none' : undefined } }}
                    >
                      <TableCell sx={{ width: 48, pr: 0 }}>
                        {canExpand && (
                          <IconButton
                            size="small"
                            aria-label={open ? 'Згорнути' : 'Розгорнути'}
                            onClick={(e) => { e.stopPropagation(); toggleGroup(row.key); }}
                          >
                            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.lot_ids.length === 1
                          ? row.lot_ids[0]
                          : `${row.lot_ids.length} партій`}
                      </TableCell>
                      <TableCell>
                        {row.items.some((it) => it.production_order_id != null || it.order_ref_display)
                          ? [...new Set(
                            row.items
                              .filter((it) => it.production_order_id != null || it.order_ref_display)
                              .map((it) => orderRefFromRow(it)),
                          )].join(', ')
                          : (row.order_ids.length
                            ? [...new Set(row.order_ids)].map((id) => formatProductionOrderRef({ productionOrderId: id })).join(', ')
                            : '—')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {row.peremol_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}
                          color={row.quantity_kg <= 0 ? 'text.disabled' : 'inherit'}>
                          {row.quantity_kg}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.quantity_initial}</TableCell>
                      <TableCell>{fmt(row.created_at)}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Button size="small" onClick={() => setDetail(row)}>Деталі %</Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 1, bgcolor: 'action.hover' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, pl: 1 }}>
                              Партії, що увійшли в групу
                            </Typography>
                            <Table size="small" sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>ID партії</TableCell>
                                  <TableCell>Виріб</TableCell>
                                  <TableCell>Залишок (кг)</TableCell>
                                  <TableCell>Початково (кг)</TableCell>
                                  <TableCell>Створено</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.items.map((lot) => (
                                  <TableRow key={lot.id}>
                                    <TableCell>{lot.id}</TableCell>
                                    <TableCell>{productLabel(lot)}</TableCell>
                                    <TableCell>{lot.quantity_kg}</TableCell>
                                    <TableCell>{lot.quantity_initial}</TableCell>
                                    <TableCell>{fmt(lot.created_at)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Tab 1: Операції ── */}
      {tab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Партія / Джерело</TableCell>
                <TableCell>Сировина</TableCell>
                <TableCell>Подано (кг)</TableCell>
                <TableCell>Вихід план (кг)</TableCell>
                <TableCell>Вихід факт (кг)</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Коментар</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">Немає операцій</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredOps.map((op) => (
                <TableRow key={op.id} hover>
                  <TableCell>{fmt(op.performed_at || op.created_at)}</TableCell>
                  <TableCell>
                    {op.waste_lot
                      ? <Typography variant="body2">{op.waste_lot_label || `Партія #${op.waste_lot}`}</Typography>
                      : op.raw_material_name
                      ? <Typography variant="body2">{op.raw_material_article ? `${op.raw_material_article} — ` : ''}{op.raw_material_name}</Typography>
                      : '—'}
                  </TableCell>
                  <TableCell>{op.raw_material_name || '—'}</TableCell>
                  <TableCell>{op.quantity_input} кг</TableCell>
                  <TableCell>{op.quantity_output_planned} кг</TableCell>
                  <TableCell>
                    {op.quantity_output_actual != null
                      ? <Typography fontWeight={600}>{op.quantity_output_actual} кг</Typography>
                      : <Typography color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={op.confirmed_at ? 'Підтверджено' : 'Очікує'}
                      color={op.confirmed_at ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>{op.comment || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Tab 2: Надходження ── */}
      {tab === 2 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Матеріал</TableCell>
                <TableCell>Операція дроблення</TableCell>
                <TableCell>К-сть план (кг)</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingArrivals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">Немає нових надходжень від дроблення</Typography>
                  </TableCell>
                </TableRow>
              ) : pendingArrivals.map((a) => {
                let name, source;
                if (a._isWasteLot) {
                  name = a.peremol_name || a.product_name || '—';
                  source = `Перемол з замовлення ${orderRefFromRow({ ...a, production_order_id: a.production_order_id ?? a.order_number })}`;
                } else {
                  const d = a.stock_item_data;
                  name = a.remelt_name
                    || (d?.raw_material_name
                      ? `${d.raw_material_article ? d.raw_material_article + ' — ' : ''}${d.raw_material_name}`
                      : d?.dye_name || d?.product_name || a.comment || '—');
                  source = `Перемол №${a.crushing_operation}`;
                }
                return (
                  <TableRow key={(a._isWasteLot ? 'lot-' : 'arr-') + a.id} hover>
                    <TableCell>{fmt(a.created_at)}</TableCell>
                    <TableCell>{name}</TableCell>
                    <TableCell>{source}</TableCell>
                    <TableCell>{a.quantity_planned}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained"
                        onClick={() => { setArrivalDlg(a); setArrivalQty(String(a.quantity_planned)); }}>
                        Прийняти
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog: склад партії */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Склад перемолу (техкарта)</DialogTitle>
        <DialogContent>
          {detail && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Назва:</strong> {detail.peremol_name || peremolDisplayName(detail.composition)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Залишок:</strong> {detail.quantity_kg} кг
                {detail.lot_ids?.length > 1 ? ` (з ${detail.lot_ids.length} партій)` : ''}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Компоненти:</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {formatComposition(detail.composition)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: підтвердити надходження */}
      <Dialog open={!!arrivalDlg} onClose={() => setArrivalDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Підтвердити надходження</DialogTitle>
        <DialogContent>
          <Typography gutterBottom sx={{ mt: 1 }}>
            {arrivalDlg?.remelt_name
              || arrivalDlg?.stock_item_data?.raw_material_name
              || arrivalDlg?.comment
              || '—'}
          </Typography>
          <TextField
            label="Кількість (факт), кг"
            type="number"
            fullWidth
            margin="normal"
            value={arrivalQty}
            onChange={(e) => setArrivalQty(e.target.value)}
            inputProps={{ step: 0.01 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrivalDlg(null)}>Скасувати</Button>
          <Button variant="contained" onClick={handleConfirmArrival}>Прийняти</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(infoDialog)} onClose={() => setInfoDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{infoDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>{infoDialog?.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferHubOpen} onClose={() => setTransferHubOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            Переміщення
            <Button
              variant="contained"
              color="warning"
              startIcon={<SwapHorizIcon />}
              onClick={openManualTransfer}
            >
              Ручне переміщення
            </Button>
          </Box>
          <IconButton edge="end" onClick={() => setTransferHubOpen(false)} aria-label="Закрити" size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            Перемістіть перемол на будь-який інший склад (той самий список, що й на інших складах).
            Рух зʼявиться в журналі; залишки оновляться одразу.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferHubOpen(false)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferOpen} onClose={() => !transferLoading && setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ручне переміщення перемолу</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {transferError && <Typography color="error">{transferError}</Typography>}
          <Autocomplete
            options={transferMaterialOptions}
            getOptionLabel={(g) => `${g.peremol_name || 'Перемол'} — ${g.quantity_kg} кг`}
            value={transferMaterialOptions.find((g) => g.key === transferGroupKey) || null}
            onChange={(_, val) => setTransferGroupKey(val ? val.key : '')}
            renderInput={(params) => <TextField {...params} label="Перемол (з поточного складу)" fullWidth />}
            ListboxProps={{ style: { maxHeight: 'min(40vh, 320px)' } }}
            noOptionsText="Немає перемолу з залишком"
          />
          <TextField
            label="Кількість, кг"
            type="number"
            value={transferQty}
            onChange={(e) => setTransferQty(e.target.value)}
            fullWidth
            inputProps={{ min: 0.001, step: 'any' }}
          />
          <TextField
            select
            label="Куди переміщуємо"
            value={
              transferDestOptions.some((w) => w.id === transferDest)
                ? transferDest
                : (transferDestOptions[0]?.id || '')
            }
            onChange={(e) => setTransferDest(e.target.value)}
            fullWidth
            SelectProps={{ MenuProps: SELECT_MENU_PROPS }}
          >
            {transferDestOptions.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Примітка"
            value={transferNote}
            onChange={(e) => setTransferNote(e.target.value)}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={transferLoading}>Скасувати</Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!transferGroupKey || !transferQty || transferLoading}
            onClick={handleManualTransfer}
          >
            {transferLoading ? <CircularProgress size={20} /> : 'Перемістити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StockCrushingWastePage;
